// Helpers for lining up two cameras' frame lists by capture time. Both
// cameras capture on the same schedule but never at the exact same second,
// and one can miss a capture -- so "the same moment" on the other camera is
// always "the closest frame it has".

const takenAtMs = (f) => f.takenAt.getTime();

/**
 * Index of the frame whose capture time is nearest to `t`, or -1 for an
 * empty list. Frames must be in capture order. `timeOf` reads the time from
 * an item; the default is a frame's takenAt.
 *
 * @param {T[]} frames
 * @param {number} t ms
 * @param {(item: T) => number} [timeOf]
 * @template T
 */
export function closestIndex(frames, t, timeOf = takenAtMs) {
  if (!frames.length) return -1;

  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (timeOf(frames[mid]) < t) lo = mid + 1;
    else hi = mid;
  }

  // `lo` is the first frame at or after t; the one before may be nearer.
  if (lo > 0 && t - timeOf(frames[lo - 1]) <= timeOf(frames[lo]) - t) return lo - 1;
  return lo;
}

/**
 * One timeline of capture moments across several cameras: every frame's time
 * from every list, sorted, with times closer together than `toleranceMs`
 * collapsed into one moment (the earlier one). Two cameras firing within a
 * few seconds of each other become a single step, and a capture only one
 * camera made still gets its own.
 *
 * @param {{takenAt: Date}[][]} lists each in capture order
 * @param {number} toleranceMs
 * @returns {number[]} ms, ascending
 */
export function mergeTimeline(lists, toleranceMs) {
  const times = lists
    .flatMap((frames) => frames.map((f) => f.takenAt.getTime()))
    .sort((a, b) => a - b);

  const out = [];
  for (const t of times) {
    if (out.length && t - out[out.length - 1] < toleranceMs) continue;
    out.push(t);
  }
  return out;
}
